import React, { useState } from 'react';
import { cn } from '../../../lib/utils';
import defaultLogo from '../../../assets/logo.svg';
import { getOptimizedImageUrl } from '@shopmuchmore/common-utils';

export type AppImageSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'full' | 'none';

export interface AppImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  src?: string;
  fallbackSrc?: string;
  isOutOfStock?: boolean;
  size?: AppImageSize;
}

export const AppImage = React.forwardRef<HTMLDivElement, AppImageProps>(
  ({ src, fallbackSrc = defaultLogo, alt, className, isOutOfStock, size = 'full', ...props }, ref) => {
    const [error, setError] = useState(false);

    const optimizedSrc = src ? getOptimizedImageUrl(src, size) : undefined;
    const displaySrc = (!error && optimizedSrc) ? optimizedSrc : fallbackSrc;
    const isFallback = Boolean(error || !src);

    const sizeClasses: Record<AppImageSize, string> = {
      xs: "w-12 h-9",
      sm: "w-16 h-12",
      md: "w-24 h-18",
      lg: "w-32 h-24",
      xl: "w-64 h-48",
      full: "w-full h-full",
      none: ""
    }

    return (
      <div 
        ref={ref}
        className={cn(
          "relative overflow-hidden bg-gray-100 shrink-0 rounded-md",
          sizeClasses[size],
          className
        )}
      >
        <img
          {...props}
          src={displaySrc}
          alt={alt || "Image"}
          onError={() => setError(true)}
          loading={props.loading || "lazy"}
          className={cn(
            "w-full h-full transition-all duration-500 rounded-lg", 
            !isFallback ? "object-contain p-1" : "object-contain p-6 opacity-30",
            isOutOfStock && "opacity-50 grayscale",
          )}
        />
      </div>
    );
  }
);
AppImage.displayName = "AppImage";
